import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Checkbox, IconButton, Skeleton, TableBody, TableCell, TableRow, Tooltip, styled, } from '@mui/material';
import ThemeContext from '@mui/material/Table/TableContext';
import { uniqueId } from 'lodash';
import React, { Children, cloneElement, forwardRef, useContext, useMemo } from 'react';
import { useCountries } from '../app/countries';
import { isReactElement } from '../helpers/is-react-element';
import { PentairIconClone, PentairIconDelete, PentairIconEdit, PentairIconInfo, PentairIconNext, PentairIconPdf, } from './pentair-icon';
import { PentairStatusBox, PentairStatusLabel } from './pentair-status';
export const TableCellIcon = forwardRef(({ onClick, disabled, value, icon, tooltip, ...props }, ref) => {
    const table = useContext(ThemeContext);
    let button = (_jsx(IconButton, { onClick: onClick, value: value, disabled: disabled, size: table?.size || 'normal', children: cloneElement(icon, { size: table?.size === 'small' ? 'sm' : 'lg' }) }));
    if (tooltip) {
        button = (_jsx(Tooltip, { title: tooltip, placement: "top", children: button }));
    }
    return (_jsx(TableCell, { ref: ref, padding: "none", align: "center", width: 48, ...props, children: button }));
});
export const TableCellEdit = styled(TableCellIcon)({});
TableCellEdit.defaultProps = { icon: _jsx(PentairIconEdit, { fontSize: 'inherit' }) };
export const TableCellPdf = styled(TableCellIcon)({});
TableCellPdf.defaultProps = { icon: _jsx(PentairIconPdf, { fontSize: 'inherit' }) };
export const TableCellInfo = styled(TableCellIcon)({});
TableCellInfo.defaultProps = { icon: _jsx(PentairIconInfo, { fontSize: 'inherit' }) };
export const TableCellArrowRight = styled(TableCellIcon)({});
TableCellArrowRight.defaultProps = { icon: _jsx(PentairIconNext, { fontSize: 'inherit' }) };
export const TableCellDelete = styled(TableCellIcon)({});
TableCellDelete.defaultProps = { icon: _jsx(PentairIconDelete, { fontSize: 'inherit' }) };
export const TableCellClone = styled(TableCellIcon)({});
TableCellClone.defaultProps = { icon: _jsx(PentairIconClone, { fontSize: 'inherit' }) };
export const TableCellStatus = ({ value, ...props }) => {
    return (_jsx(TableCell, { ...props, sx: { paddingY: 0 }, children: _jsx(PentairStatusLabel, { value: value }) }));
};
export const TableCellStatusLine = ({ value, ...props }) => {
    return (_jsx(TableCell, { padding: "none", width: 8, sx: { position: 'relative' }, ...props, children: _jsx(PentairStatusBox, { value: value, sx: { position: 'absolute', inset: 0 } }) }));
};
export const TableCellDate = ({ value, onClick, disabled, ...props }) => {
    return (_jsx(TableCell, { ...props, children: new Date(value ?? 0).toLocaleDateString(undefined, {
            day: 'numeric',
            year: 'numeric',
            month: 'short',
            hour: 'numeric',
            minute: 'numeric',
            hour12: false,
        }) }));
};
export const TableCellCountry = ({ value, render = 'label', showFlag, ...props }) => {
    const { findCountry } = useCountries();
    const country = useMemo(() => findCountry(value), [findCountry, value]);
    return (_jsxs(TableCell, { ...props, sx: { paddingY: 0, ...props.sx }, children: [showFlag && country && (_jsx("img", { loading: "lazy", width: "20", src: `https://flagcdn.com/w20/${country.code.toLowerCase()}.png`, srcSet: `https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`, alt: "", style: { marginRight: 8 } })), country?.[render] ?? value] }));
};
export const TableCellCheckbox = ({ value, checked, onChange, indeterminate, checkBoxProps, ...props }) => {
    const handleChange = (event, checked) => {
        onChange(event, checked, value);
    };
    return (_jsx(TableCell, { padding: "checkbox", width: 8, sx: { position: 'relative' }, ...props, children: _jsx(Checkbox, { indeterminate: indeterminate, checked: checked, onChange: handleChange }) }));
};
export const PentairTableBody = ({ loading, children, error, renderOnEmpty, renderOnError, loadingSkeleton, skeletonLength, ...props }) => {
    let colSpan = props.colSpan ?? 1;
    const _skeletonLength = useMemo(() => skeletonLength ?? Children.count(children), [skeletonLength, children]);
    const skeletonElem = useMemo(() => {
        if (loadingSkeleton) {
            return loadingSkeleton;
        }
        let skeletonRow = null;
        Children.forEach(children, (child) => {
            const rowProps = isReactElement(child) ? child.props : null;
            const cells = Children.map(rowProps?.children, (cell, index) => {
                const cellProps = isReactElement(cell) ? cell.props : null;
                return (_jsx(TableCell, { ...(cellProps || {}), children: _jsx(Skeleton, { animation: "wave", variant: "text" }) }, `${uniqueId('skeleton-')}${index}`));
            });
            if (colSpan < Children.count(rowProps.children)) {
                colSpan = Children.count(rowProps.children);
                skeletonRow = _jsx(TableRow, { ...rowProps, children: cells });
            }
        });
        if (!skeletonRow) {
            skeletonRow = (_jsx(TableRow, { children: Array.from(Array(colSpan).keys()).map((_, index) => (_jsx(TableCell, { children: _jsx(Skeleton, { animation: "wave", variant: "text" }) }, `${uniqueId('skeleton-')}${index}`))) }, uniqueId('skeleton-')));
        }
        return skeletonRow;
    }, [loadingSkeleton, children]);
    const skeleton = useMemo(() => Array.from(Array(_skeletonLength).keys()).map((i) => _jsx(React.Fragment, { children: skeletonElem }, i)), [_skeletonLength, skeletonElem]);
    if (loading) {
        return _jsx(TableBody, { children: skeleton });
    }
    if (error) {
        return (_jsxs(TableBody, { children: [_jsx(TableRow, { children: _jsx(TableCell, { colSpan: colSpan, align: "center", children: renderOnError ?? 'Something went wrong' }) }), error] }));
    }
    if (!Children.count(children)) {
        return (_jsxs(TableBody, { children: [_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 999, align: "center", children: renderOnEmpty ?? 'No data' }) }), error] }));
    }
    return _jsx(TableBody, { children: children });
};
